import * as React from 'react'
import { isSystemDarkMode } from 'utils/theme'
import {
  applyPreference,
  getStoredPreference,
  storePreference,
} from '@repo/studio-utils/utils/dark-mode'
import { assumeNonNull } from '@repo/studio-utils/utils/null-check'

const DarkModeContext = React.createContext<{
  preference: 'dark' | 'light'
  /** True when the experiment is forcing a specific mode — hide theme toggles. */
  isForced: boolean
  togglePreference(): void
  setPreference(value: 'dark' | 'light'): void
} | null>(null)

interface DarkModeProviderProps {
  children: React.ReactNode
  /**
   * When set by an experiment, this overrides the user's stored preference and
   * prevents the user from switching themes. The forced value is applied without
   * persisting to localStorage so it doesn't bleed into the control group if the
   * experiment is later stopped.
   */
  forcedMode?: 'dark' | 'light'
}

export function DarkModeProvider({ children, forcedMode }: DarkModeProviderProps) {
  const fallbackTheme = 'light'
  const storedPreference = getStoredPreference()
  const systemDarkMode = isSystemDarkMode()

  function getInitialPreference() {
    if (forcedMode) {
      return forcedMode
    }

    if (storedPreference) {
      return storedPreference
    }

    if (systemDarkMode) {
      return 'dark'
    }

    return fallbackTheme
  }

  const [currentPreference, setCurrentPreference] = React.useState<'light' | 'dark'>(() =>
    getInitialPreference(),
  )

  React.useEffect(() => {
    if (forcedMode) {
      applyPreference(forcedMode)
    } else {
      applyPreference(currentPreference)
    }
  }, [currentPreference, forcedMode])

  function togglePreference() {
    if (forcedMode) {
      return
    }
    const nextPreference = currentPreference === 'dark' ? 'light' : 'dark'
    storePreference(nextPreference)
    setCurrentPreference(nextPreference)
  }

  function setPreference(preference: 'dark' | 'light') {
    if (forcedMode) {
      return
    }
    storePreference(preference)
    setCurrentPreference(preference)
  }

  const preference = forcedMode ?? currentPreference

  return (
    <DarkModeContext.Provider
      value={{ preference, isForced: Boolean(forcedMode), togglePreference, setPreference }}
    >
      {children}
    </DarkModeContext.Provider>
  )
}

export function useDarkMode() {
  return assumeNonNull(React.useContext(DarkModeContext))
}
